import React, { useCallback, useMemo } from 'react';
import { MdMore } from 'react-icons/md';
import { FileDrop } from '~/components';
import { FormSelect, TextArea, FormGroup } from '~/components/form';
import { useForm } from '~/hooks';
import { useAutoSave, useLoadSection } from '~/hooks/useSection';
import sectionTypes from '../sectionTypes';
import { requiredALTA, document } from '~/api';


const initialState = {
    sectionId: 0,
    taxYear: '',
    taxYearOptions: [
        { label: '2015', value: 2015 },
        { label: '2016', value: 2016 },
        { label: '2017', value: 2017 },
        { label: '2018', value: 2018 },
        { label: '2019', value: 2019 },
        { label: '2020', value: 2020 },
        { label: '2021', value: 2021 }, 
        { label: '2022', value: 2022 }, 
    ],
    endorsements: '',
    requiredALTAOptions: [],
    ownerPolicyRecipient: '',
    lenderPolicyRecipient: '',
    attorneyCopyRecipient: '',
    additionalInfo: '',
    documents: []
};

const loadData = async (values) => {
    const endorsements = values.endorsements?.length > 0 
        ? values.endorsements 
        : values.additionalSection?.requiredALTAPrimary || [];

    if (values.requiredALTAOptions.length < 1) {

        const requiredALTAOptions = (await requiredALTA.getRequiredALTAs()).map(f => ({
            label: f.label, value: f.value
        }));
        
        return {
            ...values,
            endorsements,
            requiredALTAOptions
        };
    } 

    return { ...values, endorsements };
};

const AdditionalSection = ({ titleOpinionId, readonly, hasFileNumber }) => {

    const _readonly = useMemo(() => readonly || !hasFileNumber, [readonly, hasFileNumber]);

    const { onLoad: loadSection  } = useLoadSection(sectionTypes.ADDITIONAL, titleOpinionId, sectionTypes.TITLE_INSURANCE_APPLICATION);
    
    const onLoad = useCallback((values) => {
        return loadSection(values)
            .then(v => loadData(v));
    }, [loadSection]);
    
    const {
        state,
        fieldActions
    } = useForm({
        initialState,
        onLoad
    });
    
    const { saveField, saveDocument, deleteDocument } = useAutoSave(sectionTypes.ADDITIONAL, titleOpinionId, state.values.sectionId);

    const saveOnBlur = (value, path, validator) => {
        saveField(value, path, state.values.sectionId);
        return fieldActions.onBlur(value, path, validator);
    };

    const saveOnChange = (value, path) => {
        saveField(value, path, state.values.sectionId);
        return fieldActions.onChange(value, path);
    };

    return (
        <div className="mx-5">
            <div className="mb-5">
                <div>
                    <div className="z-10 flex items-center h-full text-xl font-semibold text-blue-60">
                        <div className="h-11 w-11 rounded-md flex items-center justify-center bg-blue-30"><MdMore size="45%"/></div>
                        <h2 className="ml-2"> Additional Information </h2>
                    </div> 
                </div>
            </div>
            <span className="block text-sm text-neutral-90 mb-2">Please note any information changes will overwrite any previously entered. Changes are saved automatically.</span>
            <div className='grid grid-cols-12 gap-8 text-sm my-8'>
                <FormGroup
                    label='Taxes Are Paid Through and Including'
                    required
                    readOnly={_readonly}
                    groupClassName='col-span-6 font-medium text-sm'
                    inputClassName='w-full border-1 border-gray-200 rounded bg-gray-50 mt-2'
                    value={state.values.taxYear}
                    error={state.errors.taxYear}
                    path='taxYear'
                    {...fieldActions}
                    onBlur={saveOnBlur}
                />
                <FormSelect
                    options={state.values.requiredALTAOptions}
                    required
                    readOnly={_readonly}
                    closeOnSelect={false}
                    isMulti
                    groupClassName='col-span-6 font-medium text-sm'
                    className="w-full mt-2 py-0.5"
                    label='Requested ALTA Endorsements'
                    value={state.values.endorsements}
                    error={state.errors.endorsements}
                    path='endorsements'
                    {...fieldActions}
                    onChange={saveOnChange}
                />
                <div className='col-span-12 mt-2'>
                    <p className='font-bold text-sm'>For any corporate entity involved in this transaction, the certifying attorney certifies as follows:</p>
                    <p className='font-bold italic text-sm mt-2'>The undersigned counsel certifies that all companies involved in this transaction were in good standing and authorized to do business in the State of North Carolina and the person signing the closing documents was authorized to do so at the time of closing.</p>
                </div>
                <TextArea
                    type='textarea'
                    readOnly={_readonly}
                    groupClassName='col-span-12'
                    inputClassName="w-full mt-2 border-1 border-gray-200 rounded bg-gray-50"
                    labelClassName='font-medium'
                    label='Send Owner’s Title Policy To'
                    value={state.values.ownerPolicyRecipient}
                    error={state.errors.ownerPolicyRecipient}
                    path='ownerPolicyRecipient'
                    {...fieldActions}
                    onBlur={saveOnBlur}
                />
                <TextArea
                    type='textarea'
                    readOnly={_readonly}
                    groupClassName='col-span-12'
                    inputClassName="w-full mt-2 border-1 border-gray-200 rounded bg-gray-50"
                    labelClassName='font-medium'
                    label='Send Lender’s Title Policy To'
                    value={state.values.lenderPolicyRecipient}
                    error={state.errors.lenderPolicyRecipient}
                    path='lenderPolicyRecipient'
                    {...fieldActions}
                    onBlur={saveOnBlur}
                />
                <TextArea
                    type='textarea'
                    readOnly={_readonly}
                    groupClassName='col-span-12'
                    inputClassName="w-full mt-2 border-1 border-gray-200 rounded bg-gray-50"
                    labelClassName='font-medium'
                    label='Send Attorney’s Copies To'
                    value={state.values.attorneyCopyRecipient}
                    error={state.errors.attorneyCopyRecipient}
                    path='attorneyCopyRecipient'
                    {...fieldActions}
                    onBlur={saveOnBlur}
                />
                <TextArea
                    type='textarea'
                    readOnly={_readonly}
                    groupClassName='col-span-12'
                    inputClassName="w-full mt-2 border-1 border-gray-200 rounded bg-gray-50"
                    labelClassName='font-medium'
                    label='Additional Information'
                    value={state.values.additionalInfo}
                    error={state.errors.additionalInfo}
                    path='additionalInfo'
                    {...fieldActions}
                    onBlur={saveOnBlur}
                />
                <FileDrop
                    files={state.values.documents}
                    readOnly={_readonly}
                    multiple
                    heading='Upload Any Additional Documents'
                    headingClassName='font-medium text-sm'
                    groupClassName='col-span-12'
                    className='h-20 mt-2'
                    onChange={files => {
                        const documents = files.map(f => {
                            return {
                                ...f,
                                type: document.DocumentType.FTO_ATTACHMENT,
                                uploaded: f.uploaded == undefined ? false : f.uploaded,
                                isDeleted: f.isDeleted != undefined ? f.isDeleted : false
                            };
                        });

                        documents.map(d => {
                            if (!d.uploaded) {
                                saveDocument(d, d.type, state.values.sectionId);
                            }
                        });

                        fieldActions.onChange(documents, 'documents');
                    }}
                    onRemove={(_, index) => {
                        const files = [...state.values.documents];
                        const deleted = files[index];
                        files.splice(index, 1);
                        deleteDocument(deleted.id, state.values.sectionId);
                        fieldActions.onChange(files, 'documents');
                    }}
                />
            </div>
        </div>
    );
};

export default AdditionalSection;

export { default as Review } from './Review';